import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";

const AUTOSAVE_DEBOUNCE_MS = 2000;
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;

/**
 * - `idle`: nothing saved yet this session
 * - `saving`: a save is in flight (including between retries)
 * - `saved`: the latest save succeeded
 * - `error`: the latest save failed after exhausting retries
 */
export type AutosaveStatus = "idle" | "saving" | "saved" | "error";

interface UseAutosaveDraftOptions {
  /** Whether there are unsaved changes to save */
  hasChanges: boolean;
  /** Whether we can autosave (e.g., existing playbook, not currently saving) */
  canAutosave: boolean;
  /** Function to perform the save - should throw on failure */
  onSave: () => Promise<void>;
}

export interface UseAutosaveDraftResult {
  /**
   * Authoritatively persists pending changes right now (clearing the debounce
   * timer): it waits for any in-flight save to settle, then saves the current
   * values if anything is still pending. Resolves to `true` once the
   * configuration is saved, or `false` if the save failed. Use before actions
   * that depend on the latest persisted state — input blur, leaving the page,
   * kicking off a preview run.
   */
  flushPendingSave: () => Promise<boolean>;
  /** Current save state, for surfacing a "Saving…" / "Saved" / error indicator. */
  saveStatus: AutosaveStatus;
  /** ISO timestamp of the most recent successful save this session, if any. */
  lastSavedAt: string | null;
}

/**
 * Hook that handles debounced autosave with retry on failure.
 *
 * - Triggers save after 2 seconds of inactivity (debounce resets on each change)
 * - Saves immediately on component unmount if there are pending changes
 * - Retries silently with exponential backoff on failure (1s, 2s, 4s)
 * - Exposes {@link UseAutosaveDraftResult.flushPendingSave} to save on demand
 */
export function useAutosaveDraft({
  hasChanges,
  canAutosave,
  onSave,
}: UseAutosaveDraftOptions): UseAutosaveDraftResult {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The save currently in flight, so an on-demand flush can chain after it
  // instead of racing it (which could pin a stale revision).
  const inFlightRef = useRef<Promise<void> | null>(null);

  const [saveStatus, setSaveStatus] = useState<AutosaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  // Performs a save, serialized so two saves never overlap (a flush can race the
  // debounced save). Tracks status; resolves on success, rejects on failure.
  const performSave = useCallback(async () => {
    const prior = inFlightRef.current;
    const run = (async () => {
      if (prior) {
        // Let an in-flight save finish first; its own caller handles its result.
        await prior.catch(() => undefined);
      }
      setSaveStatus("saving");
      await onSave();
      setLastSavedAt(new Date().toISOString());
      setSaveStatus("saved");
    })();
    inFlightRef.current = run;
    try {
      await run;
    } finally {
      if (inFlightRef.current === run) {
        inFlightRef.current = null;
      }
    }
  }, [onSave]);

  // Clear any pending timeout
  const clearPendingTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  // Event handler for scheduled save (debounce timer fired)
  const onScheduledSave = useEffectEvent(
    async (retriesLeft: number = MAX_RETRIES, delayMs: number = 0) => {
      // Wait for delay if this is a retry
      if (delayMs > 0) {
        await new Promise<void>((resolve) => {
          retryTimeoutRef.current = setTimeout(resolve, delayMs);
        });
      }

      // Check if we should still save (values may have changed during delay)
      if (!hasChanges || !canAutosave) {
        return;
      }

      try {
        await performSave();
      } catch {
        // Retry silently with exponential backoff
        if (retriesLeft > 0) {
          const nextDelay =
            delayMs === 0 ? INITIAL_RETRY_DELAY_MS : delayMs * 2;
          void onScheduledSave(retriesLeft - 1, nextDelay);
        } else {
          // All retries exhausted — surface the failure.
          setSaveStatus("error");
        }
      }
    },
  );

  // Event handler for unmount
  const onUnmount = useEffectEvent(() => {
    clearPendingTimeouts();
    if (hasChanges && canAutosave) {
      // Fire and forget — the component is unmounting — but still go through
      // `performSave` so it chains after any in-flight save via `inFlightRef`
      // instead of racing it (which could fork the revision chain). A failure
      // has no editor left to report it in, so it is swallowed rather than
      // raised as an unhandled rejection.
      void performSave().catch(() => undefined);
    }
  });

  // On-demand flush: authoritatively persist pending edits and report whether
  // the configuration ended up saved. Unlike the debounced path this is NOT
  // gated on `canAutosave` — if a save is already in flight, `performSave`
  // chains after it (rather than skipping) and then saves the data as it was
  // when the flush was called, a superset of what the in-flight save sent, so
  // callers like a preview run can rely on it being persisted before they
  // proceed. Edits made after the call go out through the debounce.
  const flushPendingSave = useCallback(async (): Promise<boolean> => {
    clearPendingTimeouts();
    try {
      if (hasChanges) {
        await performSave();
      } else if (inFlightRef.current) {
        await inFlightRef.current;
      }
      return true;
    } catch {
      setSaveStatus("error");
      return false;
    }
  }, [clearPendingTimeouts, hasChanges, performSave]);

  // Schedule autosave when hasChanges becomes true or changes continue
  useEffect(() => {
    if (!canAutosave || !hasChanges) {
      clearPendingTimeouts();
      return;
    }

    // Clear existing timeout and start new one (reset debounce)
    clearPendingTimeouts();

    timeoutRef.current = setTimeout(() => {
      void onScheduledSave();
    }, AUTOSAVE_DEBOUNCE_MS);

    return clearPendingTimeouts;
  }, [hasChanges, canAutosave, clearPendingTimeouts]);

  // Save immediately on unmount if there are pending changes
  useEffect(() => {
    return () => {
      onUnmount();
    };
  }, []);

  return { flushPendingSave, saveStatus, lastSavedAt };
}
